import { useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock } from 'lucide-react';

// Authenticated "change password while logged in" card for ProfilePage.
// On success the backend invalidates every session (incl. this device),
// so we sign the user out and let them log in again with the new password.
export const ChangePasswordCard = () => {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('New password must be at least 8 characters.');
    if (next !== confirm) return toast.error('New passwords do not match.');
    if (next === cur) return toast.error('New password must be different from your current one.');
    setBusy(true);
    try {
      const { data } = await api.post('/auth/change-password', {
        current_password: cur,
        new_password: next,
      });
      toast.success(data?.message || 'Password changed. Please sign in again.');
      setCur(''); setNext(''); setConfirm('');
      setTimeout(() => logout(), 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not change password. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="change-password-card">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Lock className="h-4 w-4" /> Password
      </h3>
      {!open ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="change-password-open-btn"
        >
          Change password
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <Input
            type="password"
            placeholder="Current password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            autoComplete="current-password"
            data-testid="change-password-current"
          />
          <Input
            type="password"
            placeholder="New password (min 8 characters)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            data-testid="change-password-new"
          />
          <Input
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="change-password-confirm"
          />
          <p className="text-[10px] text-muted-foreground">
            For your security, you&apos;ll be signed out of all devices after changing.
          </p>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy} data-testid="change-password-submit">
              {busy ? 'Changing…' : 'Update password'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
